import { useEffect, useRef, useState } from "react";
import {
  T, SC, FONT, CARD, CARD_INNER, LABEL_SM, LABEL_XS, BAR_TRACK,
} from "../theme/playbookTheme";

// ── Constants ─────────────────────────────────────────────────
const D2R   = Math.PI / 180;
const RE_KM = 6371;

// Fixed isometric camera (azimuth, elevation)
const CAM_AZ = -22 * D2R;
const CAM_EL =  30 * D2R;
const cosAz = Math.cos(CAM_AZ), sinAz = Math.sin(CAM_AZ);
const cosEl = Math.cos(CAM_EL), sinEl = Math.sin(CAM_EL);

// Deterministic star field
const STARS = Array.from({ length: 180 }, (_, k) => ({
  px: (Math.sin(k * 2.39996 + 1.1) + 1) / 2,
  py: (Math.cos(k * 1.61803 + 0.7) + 1) / 2,
  r:  0.35 + (k % 3) * 0.45,
  a:  0.12 + (k % 8) * 0.04,
}));

// ── Math helpers ──────────────────────────────────────────────
function project(x, y, z, cx, cy, sc) {
  // Rotate by azimuth (around Z)
  const r1x = x * cosAz - y * sinAz;
  const r1y = x * sinAz + y * cosAz;
  // Rotate by elevation (around X)
  const r2x = r1x;
  const r2y = r1y * cosEl - z * sinEl;
  const r2z = r1y * sinEl + z * cosEl;
  // Orthographic projection — r2z = depth into screen
  return { sx: cx + r2x * sc, sy: cy - r2y * sc, d: r2z };
}

// ECI position on circular orbit for true anomaly θ
function eciPos(theta, r, incR, raanR) {
  const xp = r * Math.cos(theta);
  const yp = r * Math.sin(theta);
  // Rotate by inclination around the node line (X of perifocal = node direction)
  const x1 = xp;
  const y1 = yp * Math.cos(incR);
  const z1 = yp * Math.sin(incR);
  // Rotate by RAAN around Z
  const cR = Math.cos(raanR), sR = Math.sin(raanR);
  return [x1 * cR - y1 * sR, x1 * sR + y1 * cR, z1];
}

// Draw a polyline through projected points
function polyline(ctx, pts, stroke, lw, dash, alpha = 1) {
  if (pts.length < 2) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lw;
  ctx.setLineDash(dash || []);
  ctx.beginPath();
  pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy)));
  ctx.stroke();
  ctx.restore();
}

// Draw arrowhead at (ex,ey) pointing in direction (dx,dy)
function arrowHead(ctx, ex, ey, dx, dy, size, color) {
  const angle = Math.atan2(dy, dx);
  ctx.save();
  ctx.fillStyle = color;
  ctx.translate(ex, ey);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(-size, -size * 0.45);
  ctx.lineTo(-size, size * 0.45);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// ═══════════════════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function OrbitalVisualizer({ miss, orb, setM }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const st        = useRef({ theta: 0, earthRot: 0, lastT: null });
  const pauseRef  = useRef(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => { pauseRef.current = paused; }, [paused]);

  // Pull orbital params (RAAN defaults to 45 if not set yet)
  const incR  = miss.inc * D2R;
  const raanR = (miss.raan ?? 45) * D2R;
  const r_km  = RE_KM + miss.alt;
  const Torb  = orb.T_orb;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Reset timing so dt doesn't spike on parameter change
    st.current.lastT = null;

    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2;
    const cy = H / 2 + 15;
    const RE_px = Math.min(W, H) * 0.175;
    const sc    = RE_px / RE_KM;
    const pr    = (x, y, z) => project(x, y, z, cx, cy, sc);

    // ── Pre-compute static geometry ──────────────────────────
    const N_ORB = 360;
    const orbPts = Array.from({ length: N_ORB + 1 }, (_, k) => {
      const th = (k / N_ORB) * 2 * Math.PI;
      const [x, y, z] = eciPos(th, r_km, incR, raanR);
      return { th, x, y, z, ...pr(x, y, z) };
    });

    // Equatorial reference ring (slightly outside Earth)
    const eqR = RE_KM * 1.02;
    const eqPts = Array.from({ length: 361 }, (_, k) => {
      const a = (k / 360) * 2 * Math.PI;
      return pr(eqR * Math.cos(a), eqR * Math.sin(a), 0);
    });

    // Vernal equinox direction (+X axis label endpoint)
    const vEq  = pr(RE_KM * 1.55, 0, 0);
    const ctr  = pr(0, 0, 0);

    // Ascending node endpoint (at raanR in equatorial plane)
    const anPt = pr(RE_KM * 1.55 * Math.cos(raanR), RE_KM * 1.55 * Math.sin(raanR), 0);

    // RAAN arc points (from vernal equinox to ascending node, on equatorial plane)
    const raanDeg = miss.raan ?? 45;
    const raanArcPts = Array.from({ length: Math.max(2, Math.abs(raanDeg) + 1) }, (_, k) => {
      const a = (k / Math.max(1, Math.abs(raanDeg))) * raanR;
      const r = RE_KM * 0.62;
      return pr(r * Math.cos(a), r * Math.sin(a), 0);
    });

    // Inclination arc at ascending node (arc from equatorial plane to orbital plane)
    const incDeg = miss.inc;
    const incArcPts = Array.from({ length: Math.max(2, Math.floor(incDeg) + 1) }, (_, k) => {
      const a = (k / Math.max(1, Math.floor(incDeg))) * incR;
      const r = RE_KM * 0.52;
      // In the plane of ascending node meridian:
      // "equatorial" unit vector at ascending node (perpendicular to node line)
      const eqUx = -Math.sin(raanR), eqUy = Math.cos(raanR);
      // "polar" unit vector
      const polUz = 1;
      // Arc point
      const x = r * eqUx * Math.cos(a);
      const y = r * eqUy * Math.cos(a);
      const z = r * polUz * Math.sin(a);
      return pr(x, y, z);
    });

    // Orbit normal vector (for orbital plane indicator)
    // Normal = node direction × satellite-at-θ=π/2 direction
    const [nx, ny, nz_] = eciPos(Math.PI / 2, RE_KM * 1.5, incR, raanR);
    const normEnd = pr(nx, ny, nz_);

    // ── Animation frame ──────────────────────────────────────
    function frame(ts) {
      if (!st.current.lastT) st.current.lastT = ts;
      const dt = Math.min((ts - st.current.lastT) / 1000, 0.05);
      st.current.lastT = ts;

      if (!pauseRef.current) {
        st.current.theta    = (st.current.theta    + (2 * Math.PI / Torb)  * 100 * dt) % (2 * Math.PI);
        st.current.earthRot = (st.current.earthRot + (2 * Math.PI / 86164) * 100 * dt) % (2 * Math.PI);
      }

      const theta    = st.current.theta;
      const earthRot = st.current.earthRot;

      // Satellite position
      const [sx, sy, sz] = eciPos(theta, r_km, incR, raanR);
      const satP = pr(sx, sy, sz);

      // Ground track (project onto Earth sphere)
      const sLen = Math.sqrt(sx * sx + sy * sy + sz * sz);
      const gtP  = pr(sx / sLen * RE_KM, sy / sLen * RE_KM, sz / sLen * RE_KM);

      // Is satellite visible (in front of Earth disk)?
      const screenDistFromCtr = Math.sqrt((satP.sx - cx) ** 2 + (satP.sy - cy) ** 2);
      const satVisible = !(screenDistFromCtr < RE_px && satP.d < 0);

      const ctx = canvas.getContext("2d");

      // ── Background + stars ─────────────────────────────────
      ctx.fillStyle = "#060b15";
      ctx.fillRect(0, 0, W, H);
      STARS.forEach(s => {
        ctx.beginPath();
        ctx.arc(s.px * W, s.py * H, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,212,255,${s.a})`;
        ctx.fill();
      });

      // ── Title / speed badge ────────────────────────────────
      ctx.fillStyle = T.t2;
      ctx.font = `500 10px ${FONT.sans}`;
      ctx.fillText("ORBITAL MECHANICS VISUALIZER", 14, 20);
      ctx.fillStyle = "#2f81f750";
      ctx.fillRect(W - 96, 8, 84, 18);
      ctx.fillStyle = T.acc;
      ctx.font = `600 10px ${FONT.sans}`;
      ctx.fillText("100× TIME LAPSE", W - 89, 20);

      // ── Equatorial circle (back) ───────────────────────────
      const eqBack  = eqPts.filter(p => p.d < 0);
      const eqFront = eqPts.filter(p => p.d >= 0);
      polyline(ctx, eqBack,  "#30363d", 1, [5, 5], 0.5);

      // ── Orbit path (back = dashed dim) ────────────────────
      const orbBack  = orbPts.filter(p => p.d < 0);
      const orbFront = orbPts.filter(p => p.d >= 0);
      polyline(ctx, orbBack, "#2f81f7", 1.5, [10, 6], 0.2);

      // ── RAAN reference: vernal equinox line ───────────────
      ctx.save();
      ctx.strokeStyle = T.yel;
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(ctr.sx, ctr.sy);
      ctx.lineTo(vEq.sx, vEq.sy);
      ctx.stroke();
      ctx.restore();

      // ── Ascending node line ────────────────────────────────
      ctx.save();
      ctx.strokeStyle = T.grn;
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.globalAlpha = 0.55;
      ctx.beginPath();
      ctx.moveTo(ctr.sx, ctr.sy);
      ctx.lineTo(anPt.sx, anPt.sy);
      ctx.stroke();
      ctx.restore();

      // ── RAAN arc ──────────────────────────────────────────
      if (raanArcPts.length > 1) {
        polyline(ctx, raanArcPts, T.yel, 1.5, [], 0.65);
        // Label
        const midRaan = raanArcPts[Math.floor(raanArcPts.length / 2)];
        ctx.save();
        ctx.fillStyle = T.yel;
        ctx.font = `600 10px ${FONT.sans}`;
        ctx.globalAlpha = 0.9;
        ctx.fillText(`Ω=${raanDeg.toFixed(0)}°`, midRaan.sx + 4, midRaan.sy - 4);
        ctx.restore();
      }

      // ── Inclination arc ───────────────────────────────────
      if (incArcPts.length > 1) {
        polyline(ctx, incArcPts, T.pur, 1.5, [], 0.75);
        const midInc = incArcPts[Math.floor(incArcPts.length / 2)];
        ctx.save();
        ctx.fillStyle = T.pur;
        ctx.font = `600 10px ${FONT.sans}`;
        ctx.globalAlpha = 0.9;
        ctx.fillText(`i=${incDeg.toFixed(1)}°`, midInc.sx + 4, midInc.sy - 4);
        ctx.restore();
      }

      // ── Earth ─────────────────────────────────────────────
      // Atmosphere halo
      const atmos = ctx.createRadialGradient(cx, cy, RE_px * 0.9, cx, cy, RE_px * 1.12);
      atmos.addColorStop(0, "rgba(47,100,220,0.18)");
      atmos.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(cx, cy, RE_px * 1.12, 0, Math.PI * 2);
      ctx.fillStyle = atmos;
      ctx.fill();

      // Earth body gradient
      const earthG = ctx.createRadialGradient(
        cx - RE_px * 0.3, cy - RE_px * 0.3, RE_px * 0.05,
        cx, cy, RE_px
      );
      earthG.addColorStop(0,   "#2b5ea8");
      earthG.addColorStop(0.35,"#1a4e8a");
      earthG.addColorStop(0.7, "#0f3060");
      earthG.addColorStop(1,   "#071525");

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, RE_px, 0, Math.PI * 2);
      ctx.fillStyle = earthG;
      ctx.fill();

      // Clipped lat/lon grid (rotates with Earth)
      ctx.clip();
      const earthR = RE_KM;

      // Longitude lines (every 30°)
      for (let lonDeg = 0; lonDeg < 360; lonDeg += 30) {
        const lonR = lonDeg * D2R + earthRot;
        const pts = Array.from({ length: 37 }, (_, k) => {
          const latR  = (k / 36) * Math.PI - Math.PI / 2;
          const cosLat = Math.cos(latR);
          return pr(earthR * cosLat * Math.cos(lonR), earthR * cosLat * Math.sin(lonR), earthR * Math.sin(latR));
        });
        polyline(ctx, pts.filter(p => p.d > 0), "rgba(255,255,255,0.07)", 0.6, [], 1);
      }
      // Latitude lines (every 30°)
      for (let latDeg = -60; latDeg <= 60; latDeg += 30) {
        const latR = latDeg * D2R;
        const cosLat = Math.cos(latR);
        const sinLat = Math.sin(latR);
        const pts = Array.from({ length: 73 }, (_, k) => {
          const lonR = (k / 72) * 2 * Math.PI + earthRot;
          return pr(earthR * cosLat * Math.cos(lonR), earthR * cosLat * Math.sin(lonR), earthR * sinLat);
        });
        const color = latDeg === 0 ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.07)";
        polyline(ctx, pts.filter(p => p.d > 0), color, latDeg === 0 ? 0.8 : 0.5, [], 1);
      }
      ctx.restore(); // end clip

      // Earth rim
      ctx.beginPath();
      ctx.arc(cx, cy, RE_px, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(70,130,220,0.45)";
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // ── Equatorial circle (front) ─────────────────────────
      polyline(ctx, eqFront, "#30363d", 1, [5, 5], 0.6);

      // ── Orbit path (front = solid bright) ─────────────────
      polyline(ctx, orbFront, "#2f81f7", 2.2, [], 0.9);

      // Orbit direction arrows at 1/4 and 3/4 positions
      [0.25, 0.75].forEach(frac => {
        const idx0 = Math.floor(frac * N_ORB);
        const idx1 = (idx0 + 4) % N_ORB;
        const p0 = orbPts[idx0], p1 = orbPts[idx1];
        if (p0.d > 0) {
          const dx = p1.sx - p0.sx, dy = p1.sy - p0.sy;
          const len = Math.sqrt(dx * dx + dy * dy);
          arrowHead(ctx, p0.sx + (dx / len) * 6, p0.sy + (dy / len) * 6, dx, dy, 7, "#2f81f7cc");
        }
      });

      // ── Ascending node (AN) marker ────────────────────────
      const anOrb = orbPts[0];
      if (anOrb.d > 0) {
        ctx.beginPath();
        ctx.arc(anOrb.sx, anOrb.sy, 4.5, 0, Math.PI * 2);
        ctx.fillStyle = T.grn;
        ctx.globalAlpha = 0.9;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.fillStyle = T.grn;
        ctx.font = `600 10px ${FONT.sans}`;
        ctx.fillText("AN", anOrb.sx + 7, anOrb.sy - 3);
      }

      // Descending node marker (half-orbit)
      const dnOrb = orbPts[Math.floor(N_ORB / 2)];
      if (dnOrb.d > 0) {
        ctx.beginPath();
        ctx.arc(dnOrb.sx, dnOrb.sy, 4.5, 0, Math.PI * 2);
        ctx.fillStyle = "#56d364";
        ctx.globalAlpha = 0.55;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.fillStyle = "#56d36488";
        ctx.font = `500 9px ${FONT.sans}`;
        ctx.fillText("DN", dnOrb.sx + 7, dnOrb.sy - 3);
      }

      // ── Altitude dimension line ────────────────────────────
      if (satVisible) {
        // Dashed line from Earth center to satellite
        ctx.save();
        ctx.strokeStyle = "#2f81f730";
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 4]);
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(satP.sx, satP.sy);
        ctx.stroke();
        ctx.restore();

        // Tick at Earth surface
        const tickP = pr(sx / sLen * RE_KM, sy / sLen * RE_KM, sz / sLen * RE_KM);
        ctx.save();
        ctx.strokeStyle = "#2f81f760";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(tickP.sx, tickP.sy, 3, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // ── Nadir line (satellite → ground track) ─────────────
      if (satVisible && gtP.d > -50) {
        ctx.save();
        ctx.strokeStyle = "#3fb95040";
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 5]);
        ctx.beginPath();
        ctx.moveTo(satP.sx, satP.sy);
        ctx.lineTo(gtP.sx, gtP.sy);
        ctx.stroke();
        ctx.restore();
      }

      // ── Ground track point ────────────────────────────────
      if (gtP.d > -80) {
        ctx.beginPath();
        ctx.arc(gtP.sx, gtP.sy, 4, 0, Math.PI * 2);
        ctx.fillStyle = T.grn;
        ctx.globalAlpha = 0.9;
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      // ── Satellite ─────────────────────────────────────────
      if (satVisible) {
        // Outer glow
        const glow = ctx.createRadialGradient(satP.sx, satP.sy, 1, satP.sx, satP.sy, 20);
        glow.addColorStop(0, "rgba(47,129,247,0.55)");
        glow.addColorStop(1, "rgba(47,129,247,0)");
        ctx.beginPath();
        ctx.arc(satP.sx, satP.sy, 20, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

        // Body
        ctx.beginPath();
        ctx.arc(satP.sx, satP.sy, 5.5, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(satP.sx, satP.sy, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = T.acc;
        ctx.fill();

        // Velocity arrow (tangent to orbit)
        const thetaIdx = Math.round((theta / (2 * Math.PI)) * N_ORB) % N_ORB;
        const nextIdx  = (thetaIdx + 3) % N_ORB;
        const nxtPt    = orbPts[nextIdx];
        if (nxtPt && nxtPt.d > 0) {
          const vdx = nxtPt.sx - satP.sx;
          const vdy = nxtPt.sy - satP.sy;
          const vlen = Math.sqrt(vdx * vdx + vdy * vdy);
          if (vlen > 0.1) {
            const vScale = 26;
            const ex = satP.sx + (vdx / vlen) * vScale;
            const ey = satP.sy + (vdy / vlen) * vScale;
            ctx.save();
            ctx.strokeStyle = T.yel;
            ctx.lineWidth = 1.5;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(satP.sx, satP.sy);
            ctx.lineTo(ex, ey);
            ctx.stroke();
            ctx.restore();
            arrowHead(ctx, ex, ey, vdx, vdy, 6, T.yel + "cc");
          }
        }

        // Altitude tag
        ctx.save();
        ctx.font = `500 10px ${FONT.sans}`;
        const tag = `${miss.alt} km`;
        const tw  = ctx.measureText(tag).width;
        ctx.fillStyle = "rgba(22,27,34,0.75)";
        ctx.fillRect(satP.sx + 10, satP.sy - 18, tw + 8, 16);
        ctx.fillStyle = T.t0;
        ctx.fillText(tag, satP.sx + 14, satP.sy - 6);
        ctx.restore();
      } else {
        // Satellite behind Earth — show indicator on rim
        const angle = Math.atan2(satP.sy - cy, satP.sx - cx);
        const rimX  = cx + Math.cos(angle) * RE_px;
        const rimY  = cy + Math.sin(angle) * RE_px;
        ctx.beginPath();
        ctx.arc(rimX, rimY, 4, 0, Math.PI * 2);
        ctx.fillStyle = "#2f81f740";
        ctx.fill();
      }

      // ── Vernal equinox label ───────────────────────────────
      ctx.save();
      ctx.fillStyle = T.yel;
      ctx.font = `500 9px ${FONT.sans}`;
      ctx.globalAlpha = 0.7;
      ctx.fillText("♈ Vernal equinox", vEq.sx + 4, vEq.sy + 3);
      ctx.restore();

      // ── Ascending node label ───────────────────────────────
      ctx.save();
      ctx.fillStyle = T.grn;
      ctx.font = `500 9px ${FONT.sans}`;
      ctx.globalAlpha = 0.65;
      ctx.fillText("Ω Ascending node", anPt.sx + 4, anPt.sy + 3);
      ctx.restore();

      // ── Footer bar ────────────────────────────────────────
      ctx.fillStyle = "rgba(6,11,21,0.7)";
      ctx.fillRect(0, H - 28, W, 28);
      ctx.fillStyle = T.t2;
      ctx.font = `500 10px ${FONT.sans}`;
      ctx.fillText(
        `i = ${miss.inc.toFixed(1)}°    Ω = ${(miss.raan ?? 45).toFixed(0)}°    ` +
        `alt = ${miss.alt} km    T = ${(orb.T_orb / 60).toFixed(1)} min    ` +
        `v = ${orb.v.toFixed(0)} m/s`,
        14, H - 10
      );

      animRef.current = requestAnimationFrame(frame);
    }

    animRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(animRef.current);
  }, [miss.inc, miss.alt, miss.raan, orb.T_orb, incR, raanR, r_km, Torb]);

  // ── Derived values for the right panel ────────────────────
  const raan = miss.raan ?? 45;

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:16, alignItems:"start" }}>

      {/* ── Canvas ──────────────────────────────────────────── */}
      <div style={{ ...CARD, padding:0, overflow:"hidden", position:"relative", background:"#060b15" }}>
        <canvas
          ref={canvasRef}
          width={720}
          height={520}
          style={{ width:"100%", display:"block" }}
        />
        {/* Overlay controls */}
        <div style={{ position:"absolute", top:10, right:12, display:"flex", gap:8 }}>
          <button onClick={() => setPaused(p => !p)} style={{
            padding:"5px 14px",
            fontFamily:FONT.sans, fontSize:11, fontWeight:600,
            background:"rgba(22,27,34,0.85)",
            border:`1px solid ${T.b1}`, borderRadius:5,
            color:T.t0, cursor:"pointer",
          }}>
            {paused ? "▶  Resume" : "⏸  Pause"}
          </button>
        </div>

        {/* Legend overlay */}
        <div style={{ position:"absolute", bottom:36, right:12,
          background:"rgba(6,11,21,0.82)", border:`1px solid ${T.b1}`,
          borderRadius:6, padding:"10px 14px" }}>
          {[
            { c:"#2f81f7", l:"Orbit path" },
            { c:T.grn,     l:"Ascending node / ground track" },
            { c:T.yel,     l:"Vernal equinox / velocity" },
            { c:T.pur,     l:"Inclination angle" },
            { c:"#30363d", l:"Equatorial reference" },
          ].map(item => (
            <div key={item.l} style={{ display:"flex", gap:8, alignItems:"center", marginBottom:5 }}>
              <div style={{ width:18, height:2, background:item.c, borderRadius:1, flexShrink:0 }} />
              <span style={{ fontFamily:FONT.sans, fontSize:10, color:T.t2 }}>{item.l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel ─────────────────────────────────────── */}
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

        {/* Orbital element inputs */}
        <div style={{ ...CARD }}>
          <div style={{ fontFamily:FONT.sans, fontSize:11, fontWeight:600,
            letterSpacing:"0.08em", textTransform:"uppercase", color:T.t1,
            borderBottom:`1px solid ${T.b1}`, paddingBottom:10, marginBottom:18 }}>
            Orbital Element Inputs
          </div>

          {[
            { label:"Inclination (i)",  key:"inc",  min:0,   max:98,  step:0.5, color:T.pur,  unit:"°",  desc:"Tilt of orbital plane to equator" },
            { label:"RAAN (Ω)",         key:"raan", min:0,   max:360, step:1,   color:T.yel,  unit:"°",  desc:"Right ascension of ascending node" },
            { label:"Altitude",         key:"alt",  min:300, max:600, step:5,   color:T.acc,  unit:"km", desc:"Orbital altitude above Earth surface" },
          ].map(f => {
            const val = f.key === "raan" ? (miss.raan ?? 45) : miss[f.key];
            return (
              <div key={f.key} style={{ marginBottom:18 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
                  <span style={{ fontFamily:FONT.sans, fontSize:12, fontWeight:500, color:T.t0 }}>{f.label}</span>
                  <span style={{ fontFamily:FONT.mono, fontSize:13, fontWeight:700, color:f.color }}>
                    {typeof val === "number" ? val.toFixed(f.step < 1 ? 1 : 0) : val}{f.unit}
                  </span>
                </div>
                <input type="range" min={f.min} max={f.max} step={f.step} value={val}
                  onChange={e => setM(f.key, Number(e.target.value))}
                  style={{ width:"100%", accentColor:f.color, height:4, cursor:"pointer", marginBottom:3 }} />
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontFamily:FONT.sans, fontSize:9, color:T.t2 }}>{f.min}{f.unit}</span>
                  <span style={{ fontFamily:FONT.sans, fontSize:9, color:T.t2 }}>{f.desc}</span>
                  <span style={{ fontFamily:FONT.sans, fontSize:9, color:T.t2 }}>{f.max}{f.unit}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Keplerian elements table */}
        <div style={{ ...CARD }}>
          <div style={{ fontFamily:FONT.sans, fontSize:11, fontWeight:600,
            letterSpacing:"0.08em", textTransform:"uppercase", color:T.t1,
            borderBottom:`1px solid ${T.b1}`, paddingBottom:10, marginBottom:14 }}>
            Keplerian Elements
          </div>
          {[
            { l:"Semi-major axis (a)",  v:`${(RE_KM + miss.alt).toLocaleString()} km`,       c:T.acc  },
            { l:"Eccentricity (e)",     v:"0.000",                                            c:T.t2   },
            { l:"Inclination (i)",      v:`${miss.inc.toFixed(2)}°`,                          c:T.pur  },
            { l:"RAAN (Ω)",            v:`${raan.toFixed(1)}°`,                               c:T.yel  },
            { l:"Arg. of perigee (ω)", v:"Undefined (circular)",                              c:T.t2   },
            { l:"True anomaly (ν)",    v:"Animated →",                                        c:T.grn  },
          ].map(row => (
            <div key={row.l} style={{ display:"flex", justifyContent:"space-between",
              alignItems:"center", padding:"7px 0", borderBottom:`1px solid ${T.b0}` }}>
              <span style={{ fontFamily:FONT.sans, fontSize:11, color:T.t2 }}>{row.l}</span>
              <span style={{ fontFamily:FONT.mono, fontSize:11, fontWeight:600, color:row.c }}>{row.v}</span>
            </div>
          ))}
        </div>

        {/* Derived orbital parameters */}
        <div style={{ ...CARD }}>
          <div style={{ fontFamily:FONT.sans, fontSize:11, fontWeight:600,
            letterSpacing:"0.08em", textTransform:"uppercase", color:T.t1,
            borderBottom:`1px solid ${T.b1}`, paddingBottom:10, marginBottom:14 }}>
            Derived Parameters
          </div>
          {[
            { l:"Orbital period",    v:`${(orb.T_orb/60).toFixed(2)} min`,                c:T.t0  },
            { l:"Orbital velocity",  v:`${orb.v.toFixed(0)} m/s`,                         c:T.t0  },
            { l:"Rev / day",         v:`${orb.revs_day.toFixed(2)}`,                      c:T.t1  },
            { l:"Eclipse / orbit",   v:`${(orb.eclipse/60).toFixed(1)} min`,              c:T.t1  },
            { l:"Sun time / orbit",  v:`${(orb.sun_time/60).toFixed(1)} min`,             c:T.yel },
            { l:"J2 RAAN drift",     v:`${orb.J2_precession.toFixed(3)}°/day`,            c:T.t2  },
            { l:"Drag force",        v:`${(orb.Fd*1e6).toFixed(2)} μN`,                  c:T.t2  },
            { l:"Atmos density",     v:`${(orb.rho*1e12).toFixed(2)} ×10⁻¹² kg/m³`,     c:T.t2  },
          ].map(row => (
            <div key={row.l} style={{ display:"flex", justifyContent:"space-between",
              alignItems:"center", padding:"7px 0", borderBottom:`1px solid ${T.b0}` }}>
              <span style={{ fontFamily:FONT.sans, fontSize:11, color:T.t2 }}>{row.l}</span>
              <span style={{ fontFamily:FONT.mono, fontSize:11, fontWeight:600, color:row.c }}>{row.v}</span>
            </div>
          ))}
        </div>

        {/* Mission planning notes */}
        <div style={{ ...CARD, background:T.bg2 }}>
          <div style={{ fontFamily:FONT.sans, fontSize:11, fontWeight:600,
            color:T.t2, marginBottom:10 }}>Mission Planning Notes</div>
          <div style={{ fontFamily:FONT.sans, fontSize:11, color:T.t2, lineHeight:1.7 }}>
            {miss.inc >= 95 && miss.inc <= 98.5 && (
              <div style={{ color:T.grn, marginBottom:6, fontWeight:500 }}>
                ✓ Sun-synchronous orbit — nodal precession matches solar drift.
              </div>
            )}
            {(miss.inc < 95 || miss.inc > 98.5) && (
              <div style={{ color:T.yel, marginBottom:6 }}>
                ⚠ Non-SSO — RAAN will drift {orb.J2_precession.toFixed(2)}°/day due to J2.
              </div>
            )}
            <div>J2 precesses RAAN at {orb.J2_precession.toFixed(3)}°/day. Over {miss.years} yr RAAN shifts {(orb.J2_precession * 365.25 * miss.years).toFixed(0)}°.</div>
            <div style={{ marginTop:6 }}>EP-ADCS corrects attitude every 133 s keeping jitter &lt;5 μrad throughout.</div>
          </div>
        </div>

      </div>
    </div>
  );
}
